import { ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import logoWithText from '/images/practivated-logo.svg';
import logoNoText from '/images/practivated-logo-no-text.svg';
import { Coach, Conversation, IdCardIcon, Lightbulb, Overview, Settings, TeamManagement } from '@/svg';
import classNames from 'classnames';
import LogoutModal from '@/pages/donors-chat/modals/Logout-modal';
import { captureEvent } from '@/helper/postHog';
import userPic from '/icons/user.svg';
import collapsed from '/icons/collapsed.svg';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/Button';
import { FaPowerOff } from 'react-icons/fa6';
import GoBackModal from '@/pages/donors-chat/modals/goback-modal';
import { navigationConfirmRoutes } from '@/_lib/constants';
import { useSimulationStore } from '@/store/useSimulationStore';
import { UserRoleEnum } from '@/types/users';
import { capitalize } from '@/helper/capitalize';
import { Tooltip } from '@nextui-org/react';
import { LuChartNoAxesCombined } from 'react-icons/lu';
import { useIntercom } from 'react-use-intercom';

interface SidebarProps {
  isExpanded: boolean;
  handleExpanded: () => void;
  toggleSidebar: () => void;
}

const Sidebar = ({ isExpanded, handleExpanded, toggleSidebar }: SidebarProps) => {
  const user = useAuthStore(state => state.user);
  const resetStore = useAuthStore(state => state.resetStore);
  const resetSimulationStore = useSimulationStore(state => state.resetStore);
  const { shutdown } = useIntercom();

  const [logoutLoading, setLogoutLoading] = useState(false);
  const [isLogoutlOpen, setIsLogoutOpen] = useState(false); // For Practies Modal
  const [pendingLink, setPendingLink] = useState('');
  const [showNavigationConfirmModal, setShowNavigationConfirmModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isActive = (path: string) => pathname === path;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    if (navigationConfirmRoutes.includes(pathname)) {
      e.preventDefault();
      setPendingLink(to);
      setShowNavigationConfirmModal(true);
    }

    if (window?.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  const onConfirmNavigation = async () => {
    // await dispatch(confirmNavigation(true));
    setShowNavigationConfirmModal(false);
    // dispatch(confirmNavigation(false));
    navigate(pendingLink);
  };

  const onCancelNavigation = () => {
    setShowNavigationConfirmModal(false);
    setPendingLink('');
  };

  const HandleLogout = async () => {
    captureEvent('User Logout', {
      email: user?.email
    });

    document.cookie = 'accessToken=; path=/; expires=0;';
    document.cookie = 'intercomToken=; path=/; expires=0;';
    document?.cookie?.split(';').forEach(c => {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
    shutdown();
    resetStore();
    resetSimulationStore();
    useAuthStore.persist.clearStorage();
    useSimulationStore.persist.clearStorage();
    navigate('/login');
  };

  const closeLogoutModal = async () => {
    setLogoutLoading(true);
    setIsLogoutOpen(false);
    await HandleLogout();
  };

  const onOpenChangeClose = () => {
    setIsLogoutOpen(false);
  };

  const SidebarLink = ({
    to,
    icon,
    label
  }: {
    to: string;
    icon: ReactNode;
    label: string;
  }) => {
    return (
      <Link
        to={to}
        className={classNames('expanded-sidebar', { active: isActive(to) }, !isExpanded && 'collapsed-sidebar')}
        onClick={e => handleLinkClick(e, to)}
      >
        {icon}
        {isExpanded && <span>{label}</span>}
      </Link>
    );
  };

  return (
    <>
      <aside
        className={classNames(
          'sidebar-main sticky top-6 mt-6 bg-[#2F2968] rounded-2xl min-h-min max-h-[calc(100vh-48px)] py-4 flex flex-col gap-4',
          isExpanded ? 'w-72' : 'collapsed-sidebar-width'
        )}
      >
        <Link to="/dashboard" className="flex justify-center px-4 mb-4">
          <img src={isExpanded ? logoWithText : logoNoText} alt="Practivated" className="size-full h-9" />
        </Link>

        <div className={classNames('flex items-center gap-2 px-4', isExpanded ? 'justify-between' : 'justify-center')}>
          {isExpanded && (
            <div className="flex items-center gap-2">
              <img src={userPic} alt="user" className="w-12 h-12 rounded-full overflow-hidden" />
              <div>
                <p className="text-base font-bold text-white truncate max-w-24">{capitalize(user?.firstName)}</p>
                {user.organizationName && (
                  <Tooltip content={user.organizationName} placement="bottom-start" showArrow>
                    <p className="text-xs text-white truncate max-w-24 leading-none">{user.organizationName}</p>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleExpanded}
            className={classNames(
              'w-10 h-10 flex justify-center items-center rounded-full bg-white/15 overflow-hidden cursor-pointer',
              isExpanded ? 'rotate-0' : 'rotate-180'
            )}
          >
            <img src={collapsed} alt="collapse" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 pl-4">
          <SidebarLink to="/dashboard" icon={<Overview />} label="Dashboard" />
          {user.role !== UserRoleEnum.MEMBER && user.organizationId && (
            <SidebarLink to="/team-management" icon={<TeamManagement />} label="Team Management" />
          )}
          <SidebarLink to="/practices" icon={<Conversation />} label="Past Conversations" />
          <SidebarLink to="/coaching" icon={<Coach />} label="Coaching with Tivy" />
          <SidebarLink to="/donors" icon={<IdCardIcon />} label="Donors" />
          <SidebarLink to="/progress" icon={<LuChartNoAxesCombined size={18} />} label="Your Progress" />
          <SidebarLink to="/settings" icon={<Settings />} label="Settings" />
          <SidebarLink to="/help" icon={<Lightbulb />} label="Help" />
        </nav>

        <div className="px-4">
          <Button
            label={isExpanded ? (logoutLoading ? 'Logging Out' : 'Log Out') : ''}
            icon={<FaPowerOff size={20} />}
            onClick={() => setIsLogoutOpen(true)}
            className="w-full p-4"
            color="orange"
          />
        </div>
      </aside>

      <LogoutModal isOpen={isLogoutlOpen} onOpenChange={closeLogoutModal} closePracticeModalPause={onOpenChangeClose} />

      <GoBackModal
        isOpen={showNavigationConfirmModal}
        onOpenChange={onConfirmNavigation}
        onOpenChangeClose={onCancelNavigation}
      />
    </>
  );
};

export default Sidebar;
